import { apiClient } from './api'

export interface UserListItem {
  id: string
  fullName: string
  firstName?: string
  middleName?: string
  surname?: string
  email: string
  phoneNumber?: string
  roles: string[]
  isActive: boolean
  emailVerified: boolean
  isLockedOut?: boolean
  isDeleted?: boolean
  lastLoginAt?: string
  createdAt: string
  updatedAt: string
  // Optional detail fields
  twoFactorEnabled?: boolean
  permissions?: string[]

  // Reversible-anonymization snapshot. When isAnonymized is true, the
  // live email/firstName/surname above are placeholder values and the
  // Original* fields hold the pre-scrub identifiers. The admin UI
  // renders these on the detail page and offers a Restore action.
  isAnonymized?: boolean
  originalEmail?: string | null
  originalFirstName?: string | null
  originalSurname?: string | null
  originalPhoneNumber?: string | null
  anonymizedAt?: string | null
  anonymizedByUserId?: string | null
}

export interface UserListResponse {
  users: UserListItem[]
  totalCount: number
  pageNumber: number
  pageSize: number
  totalPages: number
}

export interface CreateUserRequest {
  email: string
  password: string
  firstName: string
  middleName?: string
  surname: string
  phoneNumber?: string
  roles: string[]
  sendWelcomeEmail?: boolean
  requireEmailVerification?: boolean
}

export interface UpdateUserRequest {
  firstName?: string
  middleName?: string
  surname?: string
  phoneNumber?: string
  roles?: string[]
  isActive?: boolean
  emailVerified?: boolean
}

export const UserService = {
  async getUsers(params?: {
    Search?: string
    Role?: string
    IsActive?: boolean
    EmailVerified?: boolean
    IncludeAdmins?: boolean
    Page?: number
    PageSize?: number
  }): Promise<UserListResponse> {
    try {
      const response = await apiClient.get('/users', {
        params: {
          search: params?.Search,
          role: params?.Role,
          isActive: params?.IsActive,
          emailVerified: params?.EmailVerified,
          includeAdmins: params?.IncludeAdmins,
          page: params?.Page || 1,
          pageSize: params?.PageSize || 10
        }
      })

      return {
        users: response.users || [],
        totalCount: response.totalCount || 0,
        pageNumber: response.pageNumber || 1,
        pageSize: response.pageSize || 10,
        totalPages: response.totalPages || 0
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
      throw error
    }
  },

  async getUserById(id: string): Promise<UserListItem> {
    try {
      const response = await apiClient.get(`/users/${id}`)
      return response
    } catch (error) {
      console.error(`Failed to fetch user ${id}:`, error)
      throw error
    }
  },

  async createUser(data: CreateUserRequest): Promise<UserListItem> {
    try {
      const response = await apiClient.post('/users', data)
      return response
    } catch (error) {
      console.error('Failed to create user:', error)
      throw error
    }
  },

  async updateUser(id: string, data: UpdateUserRequest): Promise<UserListItem> {
    try {
      const response = await apiClient.put(`/users/${id}`, data)
      return response
    } catch (error) {
      console.error(`Failed to update user ${id}:`, error)
      throw error
    }
  },

  async toggleUserStatus(id: string, isActive: boolean): Promise<void> {
    try {
      await apiClient.post(`/users/${id}/toggle-status`, isActive)
    } catch (error) {
      console.error(`Failed to toggle user ${id} status:`, error)
      throw error
    }
  },

  // Lock a user account. Omit durationMinutes for an indefinite lock (the
  // backend uses a 10-year proxy for "forever" since the domain method
  // wants a finite value).
  async lockUser(id: string, durationMinutes?: number): Promise<void> {
    try {
      await apiClient.post(`/users/${id}/lock`, { durationMinutes })
    } catch (error) {
      console.error(`Failed to lock user ${id}:`, error)
      throw error
    }
  },

  // Clears IsLockedOut AND resets FailedLoginAttempts — the login guard
  // checks both, so the counter reset is what makes the user able to log
  // in again on their next attempt.
  async unlockUser(id: string): Promise<void> {
    try {
      await apiClient.post(`/users/${id}/unlock`)
    } catch (error) {
      console.error(`Failed to unlock user ${id}:`, error)
      throw error
    }
  },

  async deleteUser(id: string, hardDelete: boolean = false, verificationCode?: string): Promise<void> {
    try {
      // Backend defaults to soft delete (deactivate). Pass hardDelete=true to
      // physically remove the row when the admin chose "Permanent Delete" in
      // the UI. Hard delete additionally requires a verification code emailed
      // to the admin's address — request one via requestHardDeleteCode first.
      const params = new URLSearchParams()
      if (hardDelete) params.append('hardDelete', 'true')
      if (verificationCode) params.append('verificationCode', verificationCode)
      const qs = params.toString()
      const url = qs ? `/users/${id}?${qs}` : `/users/${id}`
      await apiClient.delete(url)
    } catch (error) {
      console.error(`Failed to delete user ${id}:`, error)
      throw error
    }
  },

  /**
   * Request a 6-digit verification code for hard-deleting the given user.
   * Code is emailed to the currently-authenticated admin. Single-use,
   * 10-min expiry, scoped to this exact admin+target+action.
   */
  async requestHardDeleteCode(id: string): Promise<void> {
    try {
      await apiClient.post(`/users/${id}/request-hard-delete-code`)
    } catch (error) {
      console.error(`Failed to request hard delete code for user ${id}:`, error)
      throw error
    }
  },

  /**
   * Reversible-anonymization flow — three endpoints sharing the same
   * shape as the hard-delete verification pattern:
   *   1. requestAnonymizeCode → emails a 6-digit code to the admin.
   *   2. anonymizeUser → consumes the code + scrubs PII to placeholders,
   *      snapshotting the originals on the same row.
   *   3. restoreAnonymizedUser → inverse of (2); no code needed.
   */
  async requestAnonymizeCode(id: string): Promise<void> {
    try {
      await apiClient.post(`/users/${id}/anonymize/request-code`)
    } catch (error) {
      console.error(`Failed to request anonymize code for user ${id}:`, error)
      throw error
    }
  },

  async anonymizeUser(id: string, verificationCode: string, reason?: string): Promise<void> {
    try {
      await apiClient.post(`/users/${id}/anonymize`, { verificationCode, reason })
    } catch (error) {
      console.error(`Failed to anonymize user ${id}:`, error)
      throw error
    }
  },

  async restoreAnonymizedUser(id: string): Promise<void> {
    try {
      await apiClient.post(`/users/${id}/restore-anonymized`)
    } catch (error) {
      console.error(`Failed to restore anonymized user ${id}:`, error)
      throw error
    }
  }
}
